import { Box, Button, Flex, Input, Option, Select, Text } from '@modusign/design-system';
import i18nIsoCountries from 'i18n-iso-countries';
import { type CountryCode } from 'libphonenumber-js';
import { useMemo } from 'react';

import { Survey } from '@app/containers/Modal/WelcomeSurveyModal';
import { PURPOSES } from '@app/containers/Modal/WelcomeSurveyModal/fixtures';
import { countries } from '@app/libs/countries';
import {
  getFormattedSouthKoreaPhoneNumber,
  getSouthKoreaPhoneNumberWithZero,
  removeNotNumberFromString,
} from '@app/libs/inputFormat';
import { validatePhoneNumber, validateRegularPhoneNumber } from '@app/libs/validation';

const MAX_PURPOSE_LENGTH = 100;
const countryMap = countries.map((value) => {
  return {
    value: i18nIsoCountries.getAlpha2Code(value, navigator.language.substring(0, 2)) as CountryCode,
    text: value,
  };
});

interface PhoneNumberAndPurposeStepProps {
  isWorkspaceSurveyLoading: boolean;
  survey: Survey;
  setSurvey: (update: (survey: Survey) => Survey) => void;
  submitSurvey: () => void;
}

function PhoneNumberAndPurposeStep({
  isWorkspaceSurveyLoading,
  survey,
  setSurvey,
  submitSurvey,
}: PhoneNumberAndPurposeStepProps) {
  const phoneNumberValidateMessage = useMemo(() => {
    if (!survey.phoneNumber) {
      return '전화번호를 입력해 주세요';
    }
    const { success: isValidPhoneNumber } = validatePhoneNumber(survey.phoneNumber);
    const { success: isValidRegularPhoneNumber } = validateRegularPhoneNumber(survey.phoneNumber);
    if (!isValidPhoneNumber && !isValidRegularPhoneNumber) {
      return '정확한 전화번호를 입력해 주세요';
    }
    return '';
  }, [survey.phoneNumber]);

  const isFormValid =
    !!survey.countryCode &&
    !phoneNumberValidateMessage &&
    !!survey.purpose &&
    (survey.purpose !== '기타' || !!survey.purposeExtra) &&
    survey.purpose.length <= MAX_PURPOSE_LENGTH;

  return (
    <>
      <Box fontSize={2} fontWeight="medium" mt={2}>
        이용 중 불편사항을 해결해 드리기 위해 정보를 받고 있어요.
      </Box>
      <Box mt={8}>
        <Box>
          <Text fontWeight="medium">휴대폰 또는 회사 전화번호</Text>
        </Box>
        <Flex mt={3}>
          <Box maxWidth="120px">
            <Select
              height="44px"
              required
              value={survey.countryCode}
              onChange={({ currentTarget }) =>
                setSurvey((prev) => ({
                  ...prev,
                  countryCode: currentTarget.value,
                  phoneNumber: '',
                }))
              }
            >
              {countryMap.map(({ value, text }) => (
                <Option key={value} value={value}>
                  {text}
                </Option>
              ))}
            </Select>
          </Box>
          <Box flex={1}>
            <Input
              autoFocus
              height="44px"
              isInvalid={!!phoneNumberValidateMessage}
              maxLength={30}
              ml={2}
              placeholder="- 없이 숫자만 입력"
              required
              sx={{
                '&:invalid': {
                  border: '1px solid #d00000',
                },
              }}
              type="tel"
              value={survey.phoneNumber}
              onChange={({ currentTarget }) => {
                if (survey.countryCode === 'KR') {
                  const formattedSouthKoreaPhoneNumber = getFormattedSouthKoreaPhoneNumber(
                    getSouthKoreaPhoneNumberWithZero(currentTarget.value)
                  );

                  setSurvey((prev) => ({
                    ...prev,
                    phoneNumber: formattedSouthKoreaPhoneNumber,
                  }));

                  return;
                }

                setSurvey((prev) => ({
                  ...prev,
                  phoneNumber: removeNotNumberFromString(currentTarget.value),
                }));
              }}
            />
            <Box color="#d00000" mt={1} px={3}>
              {phoneNumberValidateMessage}
            </Box>
          </Box>
        </Flex>
        <Box mt={7}>
          <Text fontWeight="medium">가입 목적</Text>
        </Box>
        <Box mt={3}>
          <Select
            color={survey.purpose ? undefined : 'textNeutralVeryLight'}
            height="44px"
            required
            value={survey.purpose}
            onChange={({ currentTarget }) =>
              setSurvey((prev) => ({ ...prev, purpose: currentTarget.value }))
            }
          >
            <Option disabled value="">
              가입 목적을 선택해 주세요.
            </Option>
            {PURPOSES.map((value) => (
              <Option key={value}>{value}</Option>
            ))}
          </Select>
          {survey.purpose === '기타' && (
            <Input
              height="44px"
              maxLength={100}
              minLength={1}
              mt={2}
              placeholder="기타 가입 목적을 입력해 주세요."
              required
              value={survey.purposeExtra}
              onChange={({ currentTarget }) =>
                setSurvey((prev) => ({ ...prev, purposeExtra: currentTarget.value }))
              }
            />
          )}
        </Box>
        <Flex alignItems="center" justifyContent="end" mt={9}>
          <Button
            disabled={!isFormValid}
            isLoading={isWorkspaceSurveyLoading}
            ml={6}
            variant="primary"
            onClick={submitSurvey}
          >
            설정 완료
          </Button>
        </Flex>
      </Box>
    </>
  );
}

export default PhoneNumberAndPurposeStep;
